import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { MagnifyingGlass, Heart, ChatCircleDots, MapPin, EnvelopeSimple, House, Sparkle } from "@phosphor-icons/react";

export default function LostGuide() {
  const { user } = useAuth();

  const steps = [
    { icon: Heart, title: "1. Créez la fiche du doudou", text: "Une photo bien nette, un prénom (le vôtre ou celui donné par l'enfant), les détails qui le distinguent : marques, texture, accessoires…" },
    { icon: MapPin, title: "2. Déclarez-le perdu", text: "Indiquez l'endroit approximatif de la disparition. Nous alertons automatiquement les familles et bonnes âmes inscrites dans le département." },
    { icon: ChatCircleDots, title: "3. Recevez les messages", text: "Si une personne pense l'avoir trouvé, elle vous contacte via un chat sécurisé. Vos coordonnées restent privées, seul votre prénom est visible." },
    { icon: House, title: "4. Retour à la maison", text: "Une fois le doudou récupéré (main propre ou point relais), cliquez sur 'Retour à la maison' — l'alerte se ferme et vous partagez, si vous le souhaitez, votre histoire." },
  ];

  return (
    <Layout>
      <SEO title="J'ai perdu un doudou — Comment le signaler" description="Perdu un doudou ? Voici comment le signaler sur Doudou Alert, gratuitement, en 4 étapes." />
      <div className="max-w-3xl mx-auto px-4 py-14">

        {/* Header */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 font-bold text-xs uppercase tracking-[0.22em]">
            <MagnifyingGlass size={14} weight="fill" /> J'ai perdu un doudou
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-800 mt-4">
            Chaque doudou <span className="text-orange-500">mérite</span> de rentrer à la maison.
          </h1>
          <p className="text-stone-600 mt-4 text-lg">
            Vous venez de vivre l'un des pires moments de parents&nbsp;? On respire. Voici comment on peut vous aider — <strong>c'est gratuit et anonyme</strong>.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-12 grid sm:grid-cols-2 gap-4">
          {steps.map((s) => (
            <div key={s.title} className="bg-white rounded-3xl border border-rose-100 p-6" data-testid={`lost-step-${s.title.slice(0, 1)}`}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-100 to-rose-100 grid place-items-center text-orange-600 mb-3">
                <s.icon size={22} weight="duotone" />
              </div>
              <p className="font-display font-black text-stone-800">{s.title}</p>
              <p className="text-sm text-stone-600 mt-1">{s.text}</p>
            </div>
          ))}
        </div>

        {/* Reassurance */}
        <div className="mt-10 bg-rose-50 rounded-3xl p-6 sm:p-8 border border-rose-100">
          <p className="font-display font-black text-stone-800 flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-orange-500" /> Ce que Doudou Alert vous garantit
          </p>
          <ul className="mt-3 space-y-2 text-sm text-stone-700 leading-relaxed">
            <li>✓ <strong>100&nbsp;% gratuit</strong> — aucun paiement demandé, aucun frais caché.</li>
            <li>✓ <strong>Anonyme</strong> — seul votre prénom apparaît publiquement.</li>
            <li>✓ <strong>Chat sécurisé</strong> — jamais d'échange direct de téléphone ou d'adresse.</li>
            <li>✓ <strong>Un renfort communautaire</strong> — les familles inscrites dans votre département reçoivent l'alerte.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          {user ? (
            <>
              <p className="text-stone-700 mb-4">Vous êtes connecté&nbsp;? Vous pouvez déclarer un doudou perdu directement depuis votre espace.</p>
              <Link to="/dashboard" data-testid="lost-guide-cta-declare">
                <Button className="bg-orange-500 hover:bg-orange-600 rounded-full px-8 py-6 font-bold text-base shadow-lg shadow-orange-500/30">
                  <Heart size={20} weight="bold" className="mr-2" /> Déclarer un doudou perdu
                </Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-stone-700 mb-4">Pour déclarer un doudou perdu, il vous faut un compte&nbsp;— création en 30 secondes, sans engagement.</p>
              <Link to="/inscription" data-testid="lost-guide-cta-register">
                <Button className="bg-orange-500 hover:bg-orange-600 rounded-full px-8 py-6 font-bold text-base shadow-lg shadow-orange-500/30">
                  <Heart size={20} weight="bold" className="mr-2" /> Créer mon compte gratuit
                </Button>
              </Link>
              <p className="text-sm text-stone-500 mt-4">
                Déjà inscrit&nbsp;? <Link to="/connexion" className="text-orange-600 font-bold">Se connecter</Link>
              </p>
            </>
          )}
          <p className="text-xs text-stone-400 mt-6 flex items-center justify-center gap-1">
            <EnvelopeSimple size={14} /> Une question ? <a href="mailto:contact@doudou-alert.fr" className="text-orange-600 font-bold ml-1">contact@doudou-alert.fr</a>
          </p>
        </div>
      </div>
    </Layout>
  );
}
